const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

class ApiService {
  constructor() {
    this.token = null;
    this.onUnauthorized = null; // Callback for 401 responses
  }

  // Register callback to handle unauthorized (401) responses
  setUnauthorizedHandler(callback) {
    this.onUnauthorized = callback;
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Get response as text first to avoid body stream issues
      const responseText = await response.text();
      
      let data;
      try {
        // Try to parse as JSON
        data = responseText ? JSON.parse(responseText) : {};
      } catch (jsonError) {
        // If JSON parsing fails, use the text as message
        data = { message: responseText || 'Invalid response from server' };
      }

      // Handle 401 Unauthorized - token expired or invalid
      if (response.status === 401) {
        // Clear the invalid token
        this.clearToken();
        
        // Trigger the unauthorized callback (will logout user)
        if (this.onUnauthorized) {
          this.onUnauthorized();
        }
        
        throw {
          status: 401,
          message: 'Session expired. Please login again.',
          data,
          isAuthError: true,
        };
      }

      if (!response.ok) {
        throw {
          status: response.status,
          message: data.message || data.error || 'An error occurred',
          data,
        };
      }

      return data;
    } catch (error) {
      if (error.status) {
        throw error;
      }
      throw {
        status: 0,
        message: error.message || 'Network error',
        data: null,
      };
    }
  }

  // Auth
  async login(email, password) {
    const response = await this.request('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    if (response.success && response.data?.token) {
      this.setToken(response.data.token);
    }
    
    return response;
  }

  logout() {
    this.clearToken();
  }

  // Shops
  async getShops(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.q) queryParams.append('q', params.q);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.city) queryParams.append('city', params.city);
    if (params.state) queryParams.append('state', params.state);
    if (params.subscriptionStatus) queryParams.append('subscriptionStatus', params.subscriptionStatus);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
    
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/shops${queryString ? `?${queryString}` : ''}`);
  }

  async getShopDetails(shopId) {
    return this.request(`/v1/shop/shop-details/${shopId}`);
  }

  async registerShop(shopData) {
    return this.request('/v1/shop/register-shop', {
      method: 'POST',
      body: JSON.stringify(shopData),
    });
  }

  // Shop Admin Info
  async updateAdminInfo(shopId, adminData) {
    return this.request(`/v1/shop/admin-info/${shopId}`, {
      method: 'PATCH',
      body: JSON.stringify(adminData),
    });
  }

  async resetAdminPassword(shopId, password, confirmPassword) {
    return this.request(`/v1/shop/admin-info/${shopId}`, {
      method: 'PATCH',
      body: JSON.stringify({ password, confirmPassword }),
    });
  }

  // Shop Agent Details
  async updateShopAgentDetails(shopId, agentData) {
    return this.request(`/v1/shop/shop-agent-details/${shopId}`, {
      method: 'PATCH',
      body: JSON.stringify(agentData),
    });
  }

  // Shop Users
  async createShopUser(userData) {
    return this.request('/v1/user/shop-user', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Payment History
  async getPaymentHistory(shopId, params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skip) queryParams.append('skip', params.skip);
    
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/shop-payment-history/${shopId}${queryString ? `?${queryString}` : ''}`);
  }

  // Subscription & Credits List
  async getSubscriptionList(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.search) queryParams.append('search', params.search);
    if (params.subscriptionStatus) queryParams.append('subscriptionStatus', params.subscriptionStatus);
    if (params.renewsOnFrom) queryParams.append('renewsOnFrom', params.renewsOnFrom);
    if (params.renewsOnTo) queryParams.append('renewsOnTo', params.renewsOnTo);
    if (params.creditBalanceLessThan) queryParams.append('creditBalanceLessThan', params.creditBalanceLessThan);
    if (params.skip !== undefined) queryParams.append('skip', params.skip);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/subscription-list${queryString ? `?${queryString}` : ''}`);
  }

  // Wallet Transactions (Payment History)
  async getWalletTransactions(params = {}) {
    const queryParams = new URLSearchParams();
    
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.direction) queryParams.append('direction', params.direction);
    if (params.type) queryParams.append('type', params.type);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/wallet/transactions${queryString ? `?${queryString}` : ''}`);
  }

  // Generate Receipt PDF URL
  getReceiptUrl(transactionId) {
    return `${API_BASE_URL}/v1/shop/wallet/generate-receipt?transaction_id=${transactionId}`;
  }

  // ============================================
  // MASTER LISTS APIs
  // ============================================

  // Master Vehicle Brands (Make-Model)
  async getMasterVehicleBrands() {
    return this.request('/v1/master/vehicle-brands');
  }

  async getMasterVehicleBrandsGrouped() {
    return this.request('/v1/master/vehicle-brands/grouped');
  }

  async updateMasterVehicleBrand(brandId, data) {
    return this.request(`/v1/master/vehicle-brands/${brandId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ============================================
  // Master Regular Services (Grouped by Category)
  // Note: API uses "reguler" spelling intentionally
  // ============================================
  
  // GET grouped regular services by category
  async getMasterRegularServicesGrouped() {
    return this.request('/v1/common/master-reguler-service-grouped');
  }

  // UPDATE category name and/or service names
  // categoryId: the categoryMongoId from GET response
  // data can include:
  //   - categoryName: string (to rename category)
  //   - services: array of { serviceId/id, serviceName } (to rename services)
  //   - serviceId + serviceName: for single service update (legacy)
  async updateMasterRegularServiceCategory(categoryId, data) {
    return this.request(`/v1/common/master-reguler-service/${categoryId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Legacy endpoints (shop-specific, keeping for backwards compatibility)
  async getRegularServices(shopId = null) {
    const queryParams = new URLSearchParams();
    if (shopId) queryParams.append('shopId', shopId);
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/regular-services${queryString ? `?${queryString}` : ''}`);
  }

  async createRegularService(serviceData) {
    return this.request('/v1/shop/regular-services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateRegularService(serviceId, serviceData) {
    return this.request(`/v1/shop/regular-services/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(serviceData),
    });
  }

  // ============================================
  // Master Maintenance Services
  // ============================================
  
  // GET all active master maintenance services
  async getMasterMaintenanceServices() {
    return this.request('/v1/common/master-maintenance-service');
  }

  // UPDATE a maintenance service
  // serviceId: the _id from GET response
  // data can include: title, intervalMiles, level, intervalTime,
  //   durationRangeMinutes: { minMinutes, maxMinutes },
  //   constRangeMinutes: { minPrice, maxPrice }, (note: "const" not "cost" - legacy typo)
  //   isActive
  async updateMasterMaintenanceService(serviceId, data) {
    return this.request(`/v1/common/master-maintenance-service/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Legacy endpoints (shop-specific, keeping for backwards compatibility)
  async getMaintenanceServices(shopId = null) {
    const queryParams = new URLSearchParams();
    if (shopId) queryParams.append('shopId', shopId);
    const queryString = queryParams.toString();
    return this.request(`/v1/shop/maintenance-services${queryString ? `?${queryString}` : ''}`);
  }

  async createMaintenanceService(serviceData) {
    return this.request('/v1/shop/maintenance-services', {
      method: 'POST',
      body: JSON.stringify(serviceData),
    });
  }

  async updateMaintenanceService(serviceId, serviceData) {
    return this.request(`/v1/shop/maintenance-services/${serviceId}`, {
      method: 'PATCH',
      body: JSON.stringify(serviceData),
    });
  }
}

export const apiService = new ApiService();
export default apiService;
